import { reactive } from 'vue'
import apiClient from '@/utils/axios'

class NotificationService {
  constructor() {
    // Create reactive data store using Vue 3's reactive
    this.state = reactive({
      notifications: [],
      unreadCount: 0,
      totalCount: 0,
      loading: false,
      polling: null,
      categories: {},
      summary: null,
      enabled: false  //  Disabled by default
    })

    // Configuration
    this.config = {
      pollInterval: 30000, // 30 seconds
      apiEndpoints: {
        base: '/notifications',
        summary: '/notifications/summary',
        category: '/notifications/category',
        markAsRead: '/notifications/{id}/read',
        markAllAsRead: '/notifications/mark-all-read',
        delete: '/notifications/{id}/delete',
        clearRead: '/notifications/clear-read/all'
      },
      enablePolling: true
    }

    // ✅ DON'T auto-init - wait for enable() call
  }

  // Check if current route is public
  isPublicRoute() {
    if (typeof window === 'undefined') return false;
    
    const publicRoutes = ['view-proposal', 'view-quotation', 'view-proforma-invoice', 'view-invoice'];
    const pathname = window.location.pathname;
    
    return publicRoutes.some(route => pathname.includes(route));
  }

  //  Enable notifications only when safe
  enable() {
    if (this.state.enabled || this.isPublicRoute()) {
      //console.log('🛡️ Notifications already enabled or public route');
      return false;
    }

    this.state.enabled = true;
    //console.log('✅ Notifications ENABLED');
    this.init();
    return true;
  }

  //  Disable notifications
  disable() {
    if (!this.state.enabled) return;
    
    this.state.enabled = false;
    this.stopPolling();
    this.resetState();
    //console.log('🛡️ Notifications DISABLED');
  }

  // Initialize service (only when enabled)
  async init() {
   if (!this.state.enabled || this.isPublicRoute()) return;

    try{
     await this.fetchSummary();
     if(this.config.enablePolling){
      this.startPolling();
     }
    }catch(error){
     console.error('Failed to initialize notification service:', error);
    }
  }

  // Get all notifications with filters
  async fetchNotifications(options = {}) {
    if (!this.state.enabled || this.isPublicRoute()) {
      return { notifications: [], pagination: {}, counts: {} };
    }

    try {
      this.state.loading = true;
      
      const params = {
        per_page: options.perPage || 15,
        status: options.status || 'all',
        category: options.category || null,
        page: options.page || 1
      };

      const response = await apiClient.get(this.config.apiEndpoints.base, { params });
      
      const data = response.data.status === 1 ? response.data.data : response.data;
      
      this.state.notifications = data.notifications || [];
      this.state.unreadCount = data.counts?.unread || 0;
      this.state.totalCount = data.counts?.total || 0;
      
      return {
        notifications: this.state.notifications,
        pagination: data.pagination,
        counts: data.counts
      };
    } catch (error) {
      this.handleApiError('Failed to fetch notifications', error);
      throw error;
    } finally {
      this.state.loading = false;
    }
  }

  // Get notification summary
  async fetchSummary() {
    if (!this.state.enabled || this.isPublicRoute()) return null;

    try {
      const response = await apiClient.get(this.config.apiEndpoints.summary);
      const data = response.data.status === 1 ? response.data.data : response.data;
      
      this.state.summary = data;
      this.state.unreadCount = data.unread_notifications || 0;
      this.state.totalCount = data.total_notifications || 0;
      this.state.categories = data.categories || {};
      
      return data;
    } catch (error) {
      this.handleApiError('Failed to fetch summary', error);
      throw error;
    }
  }

  // Get notifications by category
  async fetchByCategory(category, page = 1) {
    if (!this.state.enabled || this.isPublicRoute()) return null;

    try {
      this.state.loading = true;
      
      const response = await apiClient.get(`${this.config.apiEndpoints.category}/${category}`, {
        params: { page }
      });
      
      const data = response.data.status === 1 ? response.data.data : response.data;
      return data;
    } catch (error) {
      this.handleApiError('Failed to fetch category notifications', error);
      throw error;
    } finally {
      this.state.loading = false;
    }
  }

  // Mark notification as read
  async markAsRead(notificationId) {
    if (!this.state.enabled || this.isPublicRoute()) return false;

    try {
      const endpoint = this.config.apiEndpoints.markAsRead.replace('{id}', notificationId);
      const response = await apiClient.patch(endpoint);
      
      const notification = this.state.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.is_read = true;
        notification.read_at = new Date().toISOString();
        this.state.unreadCount = Math.max(0, this.state.unreadCount - 1);
      }
      
      const data = response.data.status === 1 ? response.data.data : response.data;
      return data;
    } catch (error) {
      this.handleApiError('Failed to mark as read', error);
      throw error;
    }
  }

  // Mark all notifications as read
  async markAllAsRead() {
    if (!this.state.enabled || this.isPublicRoute()) return false;

    try {
      const response = await apiClient.patch(this.config.apiEndpoints.markAllAsRead);
      
      this.state.notifications.forEach(notification => {
        notification.is_read = true;
        notification.read_at = new Date().toISOString();
      });
      this.state.unreadCount = 0;
      
      const data = response.data.status === 1 ? response.data.data : response.data;
      return data;
    } catch (error) {
      this.handleApiError('Failed to mark all as read', error);
      throw error;
    }
  }

  // Delete notification
  async deleteNotification(notificationId) {
    if (!this.state.enabled || this.isPublicRoute()) return false;

    try {
      const endpoint = this.config.apiEndpoints.delete.replace('{id}', notificationId);
      await apiClient.delete(endpoint);
      
      const index = this.state.notifications.findIndex(n => n.id === notificationId);
      if (index !== -1) {
        const notification = this.state.notifications[index];
        if (!notification.is_read) {
          this.state.unreadCount = Math.max(0, this.state.unreadCount - 1);
        }
        this.state.notifications.splice(index, 1);
        this.state.totalCount = Math.max(0, this.state.totalCount - 1);
      }
      
      return true;
    } catch (error) {
      this.handleApiError('Failed to delete notification', error);
      throw error;
    }
  }

  // Clear all read notifications
  async clearReadNotifications() {
    if (!this.state.enabled || this.isPublicRoute()) return false;

    try {
      const response = await apiClient.delete(this.config.apiEndpoints.clearRead);
      
      this.state.notifications = this.state.notifications.filter(n => !n.is_read);
      this.state.totalCount = this.state.notifications.length;
      
      const data = response.data.status === 1 ? response.data.data : response.data;
      return data;
    } catch (error) {
      this.handleApiError('Failed to clear read notifications', error);
      throw error;
    }
  }

  // Start polling for new notifications
  startPolling() {
    if (!this.state.enabled || this.isPublicRoute()) {
      //console.log('🛡️ Skipping polling - disabled or public route');
      return;
    }

    if (this.state.polling) {
      clearInterval(this.state.polling);
    }

    this.state.polling = setInterval(async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (token && this.state.enabled && !this.isPublicRoute()) {
          await this.fetchSummary();
        } else {
          this.stopPolling();
        }
      } catch (error) {
        if (error.type === 'unauthenticated') {
          this.stopPolling();
        } else if (error.type !== 'network') {
          console.warn('Notification polling failed:', error.message);
        }
      }
    }, this.config.pollInterval);
  }

  // Stop polling
  stopPolling() {
    if (this.state.polling) {
      clearInterval(this.state.polling);
      this.state.polling = null;
    }
  }

  // Update polling interval
  updatePollingInterval(interval) {
    this.config.pollInterval = interval;
    if (this.state.polling) {
      this.stopPolling();
      this.startPolling();
    }
  }

  // Handle API errors
  handleApiError(context, error) {
    console.error(`${context}:`, error);
    
    switch (error.type) {
      case 'unauthenticated':
        this.stopPolling();
        break;
      case 'validation':
        console.error('Validation errors:', error.errors);
        break;
      case 'general':
        console.error('API Error:', error.message);
        break;
      case 'network':
        console.error('Network Error:', error.message);
        break;
      default:
        console.error('Unknown error:', error);
    }
  }

  // Get formatted notification count
  getFormattedCount(count) {
    if (count > 99) return '99+';
    return count.toString();
  }

  // Group notifications by date
  groupNotificationsByDate(notifications) {
    const groups = {};
    
    notifications.forEach(notification => {
      const date = new Date(notification.created_at).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(notification);
    });
    
    return groups;
  }

  // Filter notifications by type
  filterNotifications(notifications, filters = {}) {
    let filtered = [...notifications];
    
    if (filters.category) {
      filtered = filtered.filter(n => n.category === filters.category);
    }
    
    if (filters.priority) {
      filtered = filtered.filter(n => n.priority === filters.priority);
    }
    
    if (filters.read !== undefined) {
      filtered = filtered.filter(n => n.is_read === filters.read);
    }
    
    return filtered;
  }

  // Get state (for reactive access)
  getState() {
    return this.state;
  }

  // Reset state (useful for logout)
  resetState() {
    this.state.notifications = [];
    this.state.unreadCount = 0;
    this.state.totalCount = 0;
    this.state.categories = {};
    this.state.summary = null;
    this.stopPolling();
  }

  // Destroy service
  destroy() {
    this.disable();
  }
}

// Create singleton instance
export default new NotificationService();
